<template>
  <!-- When a task is selected -->
  <v-card v-if="task" class="task-detail-shell pa-4" elevation="1">
    <!-- Header -->

    <v-card-title class="task-detail-header py-3 px-4">
      <div class="task-detail__eyebrow">Task</div>
      <div class="d-flex align-center w-100">
        <!-- Leading icon / status dot -->
        <v-avatar size="36" class="mr-3 elevation-1" :color="task.statusColour">
          <v-icon size="20" color="white">mdi-briefcase-outline</v-icon>
        </v-avatar>

        <!-- Title + meta -->
        <div class="flex-grow-1">
          <div class="d-flex align-center text-truncate">
            <span v-if="!isEditing" class="task-detail__title text-h6 font-weight-medium mr-2 text-truncate" :title="task.title">
              {{ task.title }}
            </span>
            <v-text-field
              v-else
              v-model="draft.title"
              label="Title"
              density="comfortable"
              hide-details
              variant="outlined"
              class="text-h6 mr-2"
              style="max-width: 300px"
            />
            <audit-field-timeline
              v-if="task && task.taskId"
              entity-type="Task"
              :record-id="task.taskId"
              field-key="Title"
              field-label="Title"
            />

            <!-- Chips or Selects -->
            <div
              class="d-flex align-center flex-wrap"
              style="gap: 6px; margin-left: 10px; margin-top: 6px"
            >
              <!-- READ-ONLY CHIPS -->
              <template v-if="!isEditing">
                <v-chip
                  size="x-small"
                  variant="elevated"
                  :color="priorityColorChip(task)"
                  text-color="white"
                >
                  <v-icon start size="14">mdi-circle-outline</v-icon>
                  Priority: {{ priorityLabel(task) }}
                </v-chip>
                <audit-field-timeline
                  v-if="task && task.taskId"
                  entity-type="Task"
                  :record-id="task.taskId"
                  field-key="PriorityFK"
                  field-label="Priority"
                />

                <v-chip
                  size="x-small"
                  variant="elevated"
                  :color="statusColorChip(task)"
                  text-color="white"
                >
                  <v-icon start size="14">mdi-circle-outline</v-icon>
                  Status: {{ statusLabel(task) }}
                </v-chip>
                <audit-field-timeline
                  v-if="task && task.taskId"
                  entity-type="Task"
                  :record-id="task.taskId"
                  field-key="StatusFK"
                  field-label="Status"
                />
              </template>

              <!-- EDIT SELECTS -->
              <template v-else>
                <v-select
                  v-model="draft.priorityId"
                  :items="priorities"
                  item-title="priority"
                  :item-value="(p) => Number(p.priorityId || p.PriorityId)"
                  label="Priority"
                  density="comfortable"
                  hide-details
                  class="mr-2"
                  style="max-width: 180px"
                  :disabled="!priorities || priorities.length === 0"
                >
                  <template v-if="!priorities || priorities.length === 0" #prepend-item>
                    <v-list-item title="No priorities available" disabled />
                  </template>
                </v-select>
                <v-select
                  v-model="draft.statusId"
                  :items="statuses"
                  item-title="status"
                  :item-value="(s) => Number(s.statusId || s.StatusId)"
                  label="Status"
                  density="comfortable"
                  hide-details
                  style="max-width: 180px"
                  :disabled="!statuses || statuses.length === 0"
                >
                  <template v-if="!statuses || statuses.length === 0" #prepend-item>
                    <v-list-item title="No statuses available" disabled />
                  </template>
                </v-select>
              </template>
            </div>
          </div>

          <div class="text-caption text-medium-emphasis mt-1 d-flex align-center flex-wrap">
            <!--  <span class="mr-2">{{ task.zoneNameFK }}</span>
            <span class="mx-2">•</span>
           <span>Due {{ formatDate(task.dueDate) }}</span>-->
            <template v-if="task.assignedToFK">
              <!--
                <span class="mx-2">•</span>
               <v-avatar size="18" class="mr-1 bg-avatar">
                <v-img :src="getUserIconUrl(task.assignedToIcon)" />
              </v-avatar>
              <span>{{ task.assignedToFK }}</span>
              -->
            </template>
          </div>
        </div>

        <!-- Actions -->
        <!-- Header actions: edit / cancel / save / delete -->
        <div class="d-flex align-center task-detail__header-actions">
          <audit-history-button
            v-if="task && task.taskId"
            entity-type="Task"
            :record-id="task.taskId"
            title="Task Change History"
            class="mr-2"
          />
          <template v-if="!isEditing">
            <v-btn
              size="small"
              color="green"
              variant="flat"
              class="mr-2 task-detail__action-btn task-detail__action-btn--complete"
              @click="openCompleteDialog"
              :disabled="['3', '4'].includes(String(task.status ?? ''))"
            >
              <v-icon start size="18">mdi-check-circle</v-icon>
              Mark Complete
            </v-btn>
            <v-btn size="small" variant="tonal" color="primary" class="mr-2" @click="beginEdit">
              <v-icon start size="18">mdi-pencil-outline</v-icon>
              Edit
            </v-btn>
            <v-btn
              size="small"
              variant="tonal"
              color="error"
              class="mr-2 task-detail__action-btn"
              @click="confirmDelete"
            >
              <v-icon start size="18">mdi-delete-outline</v-icon>
              Delete
            </v-btn>
          </template>
          <template v-else>
            <v-btn size="small" variant="tonal" color="grey" class="mr-2 task-detail__action-btn" @click="cancelEdit">
              <v-icon start size="18">mdi-close</v-icon> Cancel
            </v-btn>
            <v-btn size="small" color="green" variant="flat" class="task-detail__action-btn" @click="saveEdit">
              <v-icon start size="18">mdi-content-save</v-icon> Save
            </v-btn>
          </template>

          <v-btn v-if="closable" icon variant="text" @click="$emit('close')" :aria-label="'Close'">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card-title>

    <!-- Modern accent line instead of <hr/> -->
    <div
      class="header-accent header-accent--strong mb-3"
      :style="{
        background: `linear-gradient(90deg, ${task.priorityColour}, ${task.statusColour})`,
      }"
    />

    <!-- Link to Finding / Asset / Work Order -->
    <div v-if="(task.findingId || task.FindingId) || (task.assetId || task.AssetId) || (task.workOrderId || task.WorkOrderId)" class="d-flex align-center gap-1 mb-2 task-detail__link-row">
      <v-tooltip v-if="task.findingId || task.FindingId" text="View Finding" location="top">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            size="x-small"
            variant="text"
            @click="goToFinding"
          >
            <v-icon size="14">mdi-file-find-outline</v-icon>
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip v-if="task.assetId || task.AssetId" text="View Asset" location="top">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            size="x-small"
            variant="text"
            @click="goToAsset"
          >
            <v-icon size="14">mdi-cog-outline</v-icon>
          </v-btn>
        </template>
      </v-tooltip>
      <v-tooltip v-if="task.workOrderId || task.WorkOrderId" text="View Work Order" location="top">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            size="x-small"
            variant="text"
            @click="goToWorkOrder"
          >
            <v-icon size="14">mdi-clipboard-list-outline</v-icon>
          </v-btn>
        </template>
      </v-tooltip>
    </div>

    <!-- Zone breadcrumbs + due -->
    <div class="text-caption text-grey-darken-1 task-detail__breadcrumbs">
      <v-breadcrumbs class="text-grey-darken-1 compact">
        <template #prepend>
          <v-icon size="14" class="mr-1">mdi-map-marker</v-icon>
        </template>
        <v-breadcrumbs-item>{{ task.zoneNameFK }}</v-breadcrumbs-item>
        <v-breadcrumbs-divider>›</v-breadcrumbs-divider>
        <v-breadcrumbs-item>{{ task.inspectionNameFK }}</v-breadcrumbs-item>
        <v-breadcrumbs-divider>›</v-breadcrumbs-divider>
        <v-breadcrumbs-item>{{ task.findingNameFK }}</v-breadcrumbs-item>
        <v-breadcrumbs-divider>›</v-breadcrumbs-divider>
        <v-breadcrumbs-item>{{ task.title }}</v-breadcrumbs-item>
      </v-breadcrumbs>
    </div>

    <!-- Header + chips in one line -->
    <div class="d-flex align-center mb-3 flex-wrap task-detail__meta-row">
      <!-- Left: created / due -->
      <div class="text-caption text-grey-darken-1" style="margin-left: 33px; margin-top: 4px">
        Created: <span style="color: black">{{ formatDateTime(task.createdOn) }}</span> / Due Date:
        <span style="color: black">{{ formatDate(task.dueDate) }}</span>
      </div>

      <!-- Linkage buttons (discrete) -->
      <div class="d-flex align-center ml-3" style="gap: 2px">
        <v-tooltip v-if="task.findingId || task.FindingId" text="View Finding" location="bottom">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              size="x-small"
              variant="text"
              density="comfortable"
              @click="goToFinding"
            >
              <v-icon size="18">mdi-file-find-outline</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        <v-tooltip v-if="task.assetId || task.AssetId" text="View Asset" location="bottom">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              size="x-small"
              variant="text"
              density="comfortable"
              @click="goToAsset"
            >
              <v-icon size="18">mdi-cog-outline</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        <v-tooltip v-if="task.workOrderId || task.WorkOrderId" text="View Work Order" location="bottom">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              size="x-small"
              variant="text"
              density="comfortable"
              @click="goToWorkOrder"
            >
              <v-icon size="18">mdi-clipboard-list-outline</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </div>

      <v-spacer />
      <!-- Push edit button to far right
      <v-btn icon size="small" variant="text" @click="$emit('request-edit', task)">
        <v-icon size="20">mdi-pencil-outline</v-icon>
      </v-btn> -->
    </div>

    <!-- Two-Column Layout -->
    <v-row class="mt-2" no-gutters>
      <!-- LEFT COLUMN: Main Task Information -->
      <v-col cols="12" md="7" class="pr-md-4">
        <!-- Description Card -->
        <v-card variant="outlined" class="mb-4 detail-section-card" elevation="0">
          <v-card-text>
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2" size="20">mdi-text</v-icon>
              <div class="v-label">Description</div>
              <audit-field-timeline
                v-if="task && task.taskId"
                entity-type="Task"
                :record-id="task.taskId"
                field-key="Description"
                field-label="Description"
              />
            </div>
            <div v-if="!isEditing" class="text-body-2">
              {{ task.description || "No description provided." }}
            </div>
            <v-textarea
              v-else
              v-model="draft.description"
              label="Description"
              auto-grow
              density="comfortable"
              hide-details
              variant="outlined"
            />
          </v-card-text>
        </v-card>

        <!-- Task Details Card -->
        <v-card variant="outlined" class="mb-4 detail-section-card" elevation="0">
          <v-card-text>
            <div class="d-flex align-center mb-3">
              <v-icon class="mr-2" size="20">mdi-information</v-icon>
              <div class="v-label">Task Details</div>
            </div>
            <v-row dense>
              <!-- Assigned To -->
              <v-col cols="12" sm="6">
                <div class="v-label mb-1 d-flex align-center">
                  Assigned to
                  <audit-field-timeline
                    v-if="task && task.taskId"
                    entity-type="Task"
                    :record-id="task.taskId"
                    field-key="AssignedToFK"
                    field-label="Assigned To"
                  />
                </div>
                <div v-if="!isEditing" class="d-flex align-center">
                  <v-avatar size="32" class="mr-2 bg-avatar">
                    <v-img :src="getUserIconUrl(task.assignedToIcon)" />
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">{{ task.assignedToFK || "Unassigned" }}</span>
                </div>
                <v-select
                  v-else
                  v-model="draft.assignedToUserId"
                  :items="assignees"
                  item-title="userName"
                  item-value="id"
                  label="Assigned to"
                  density="comfortable"
                  clearable
                  variant="outlined"
                  hide-details
                >
                  <template #prepend-item>
                    <v-list-item title="Select assignee" />
                    <v-divider />
                  </template>
                  <template #item="{ item, props }">
                    <v-list-item v-bind="props">
                      <template #prepend>
                        <v-avatar size="28" class="mr-2">
                          <v-img :src="getUserIconUrl(item.raw.iconId)" />
                        </v-avatar>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>

              <!-- Due Date -->
              <v-col cols="12" sm="6">
                <div class="v-label mb-1 d-flex align-center">
                  Due Date
                  <audit-field-timeline
                    v-if="task && task.taskId"
                    entity-type="Task"
                    :record-id="task.taskId"
                    field-key="DueDate"
                    field-label="Due Date"
                  />
                </div>
                <div v-if="!isEditing" class="d-flex align-center">
                  <v-icon size="16" class="mr-1" color="grey">mdi-calendar</v-icon>
                  <span class="text-body-2">{{ formatDate(task.dueDate) }}</span>
                </div>
                <v-text-field
                  v-else
                  v-model="draft.dueDate"
                  type="date"
                  density="comfortable"
                  hide-details
                  variant="outlined"
                />
              </v-col>

              <!-- Estimated Cost -->
              <v-col cols="12" sm="6">
                <div class="v-label mb-1 d-flex align-center">
                  Estimated Cost
                  <audit-field-timeline
                    v-if="task && task.taskId"
                    entity-type="Task"
                    :record-id="task.taskId"
                    field-key="EstimatedCost"
                    field-label="Estimated Cost"
                  />
                </div>
                <div v-if="!isEditing" class="d-flex align-center">
                  <span class="text-body-2 font-weight-medium">
                    €{{ task.estimatedCost?.toFixed(2) ?? "0.00" }}
                  </span>
                </div>
                <v-text-field
                  v-else
                  v-model.number="draft.estimatedCost"
                  type="number"
                  step="0.01"
                  prefix="€"
                  density="comfortable"
                  hide-details
                  variant="outlined"
                />
              </v-col>

              <!-- Location Coordinates -->
              <v-col cols="12" sm="6">
                <div class="v-label mb-1">Location</div>
                <CoordinateDisplay :lat="task.latitude" :lon="task.longitude" layout="inline" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

      </v-col>

      <!-- RIGHT COLUMN: Secondary Information -->
      <v-col cols="12" md="5">
        <!-- Map Card -->
        <v-card variant="outlined" class="mb-4 detail-section-card" elevation="0">
          <v-card-text>
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2" size="20">mdi-map</v-icon>
              <div class="v-label">Map</div>
            </div>
            <div v-if="isEditing" class="mb-2">
              <v-btn
                size="small"
                variant="tonal"
                color="primary"
                :loading="capturingLocation"
                @click="captureLocation"
              >
                <v-icon start size="18">mdi-crosshairs-gps</v-icon>
                Capture GPS
              </v-btn>
            </div>
            <div style="height: 250px; border-radius: 8px; overflow: hidden">
              <!-- read-only -->
              <zone-map-simple
                v-if="!isEditing && task.latitude && task.longitude"
                :key="task.taskId"
                :center="[task.latitude, task.longitude]"
                :zoom="12"
                :marker="[task.latitude, task.longitude]"
              />
              <!-- editable map -->
              <div
                v-else
                ref="editMap"
                style="height: 100%; width: 100%; border-radius: 8px; overflow: hidden"
              ></div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Media Gallery Card -->
        <v-card variant="outlined" class="mb-4 detail-section-card" elevation="0">
          <v-card-text>
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2" size="20">mdi-image-multiple</v-icon>
              <div class="v-label">Media</div>
            </div>

            <!-- Add images (edit mode) -->
            <div v-if="isEditing" class="mb-3">
              <v-file-input
                :model-value="draftNewPhotoFiles"
                @update:model-value="onDraftNewPhotosChange"
                accept="image/*"
                multiple
                show-size
                prepend-icon="mdi-image-plus"
                density="comfortable"
                clearable
                variant="outlined"
                label="Add images"
              />
            </div>

            <v-row dense>
              <v-col
                v-for="photo in effectiveMedia"
                :key="photo.photoUrlId || photo.url"
                cols="4"
                class="d-flex flex-column align-center"
              >
                <v-sheet
                  elevation="1"
                  rounded
                  class="pa-0"
                  style="overflow: hidden; cursor: pointer"
                  @click="openLightbox(photo.url)"
                >
                  <v-img :src="photo.url" height="80" width="80" elevation="6" class="rounded" cover />
                </v-sheet>
                <div
                  v-if="formatTaken(photo)"
                  class="text-caption text-grey text-center mt-1"
                  style="max-width: 80px; line-height: 1.1"
                  :title="formatTakenFull(photo)"
                >
                  {{ formatTaken(photo) }}
                </div>
              </v-col>

              <v-col v-if="effectiveMedia.length === 0" cols="12" class="text-center text-grey py-4">
                <v-icon size="32" class="mb-2">mdi-image-off</v-icon>
                <div class="text-caption">No images</div>
              </v-col>
            </v-row>

            <!-- Lightbox -->
            <v-dialog v-model="lightbox.show" max-width="90%" persistent>
              <v-card>
                <v-img :src="lightbox.img" aspect-ratio="1.5" max-height="80vh" contain />
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="lightbox.show = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-card-text>
        </v-card>

        <!-- Comments Card -->
        <v-card variant="outlined" class="mb-4 detail-section-card" elevation="0">
          <v-card-text>
            <div class="d-flex align-center mb-3">
              <v-icon class="mr-2" size="20">mdi-comment-text</v-icon>
              <div class="v-label">Comments</div>
              <v-chip v-if="comments.length > 0" size="x-small" class="ml-2">
                {{ comments.length }}
              </v-chip>
            </div>

            <!-- Comments List -->
            <div class="comments-pane" style="max-height: 300px; overflow-y: auto">
              <v-list density="compact" v-if="comments.length">
                <v-list-item
                  v-for="comment in comments"
                  :key="comment.commentId || comment.CommentId"
                  class="px-0"
                >
                  <template #prepend>
                    <v-avatar size="28" class="mr-2 bg-avatar">
                      <v-img :src="getUserIconUrl(comment.userIconId)" />
                    </v-avatar>
                  </template>
                  <div class="comment-body">
                    <div class="d-flex align-center mb-1">
                      <span class="font-weight-medium">{{ comment.userName || task.assignedToFK }}</span>
                      <span class="text-caption text-grey ml-2">
                        {{ formatDateTime(comment.commentDate) }}
                      </span>
                    </div>
                    <div class="text-body-2 comment-text">{{ comment.comment }}</div>
                  </div>
                </v-list-item>
              </v-list>

              <div v-else class="text-center text-grey py-4">
                <v-icon size="32" class="mb-2">mdi-comment-outline</v-icon>
                <div class="text-caption">No comments yet</div>
              </div>
            </div>

            <!-- New Comment Input -->
            <div class="mt-3">
              <v-textarea
                v-model="newComment"
                label="Add a comment"
                variant="outlined"
                rows="2"
                auto-grow
                density="comfortable"
                placeholder="Type a comment..."
                hide-details
                @keydown.enter.exact.prevent="submitComment"
              />
              <div class="d-flex justify-end mt-2">
                <v-btn
                  color="primary"
                  size="small"
                  variant="flat"
                  :disabled="!newComment.trim()"
                  @click="submitComment"
                >
                  <v-icon start size="16">mdi-send</v-icon>
                  Post
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Delete Task Dialog -->
    <v-dialog v-model="deleteDialog.show" max-width="500px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span class="text-h6 font-weight-bold">Delete Task</span>
          <v-btn icon variant="text" @click="deleteDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-2">
            <v-icon color="error" size="48" class="mb-2">mdi-alert-circle</v-icon>
          </div>
          <div class="text-h6 mb-2">Are you sure you want to delete this task?</div>
          <div class="text-body-2 text-grey">
            <strong>{{ task.title }}</strong>
          </div>
          <div class="text-body-2 text-grey mt-2">
            This action cannot be undone. All associated comments and media will also be removed.
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="deleteDialog.show = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="deleteTask">
            <v-icon start>mdi-delete</v-icon> Delete Task
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Complete Task Dialog -->
    <v-dialog v-model="completeDialog.show" max-width="500px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span class="text-h6 font-weight-bold">Complete Task</span>
          <v-btn icon variant="text" @click="completeDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-4">
            <label class="font-weight-medium">Add photo (Required)</label>
            <v-file-input
              :model-value="completePhotoFiles"
              prepend-icon="mdi-plus"
              accept="image/*"
              multiple
              show-size
              outlined
              dense
              @update:model-value="handlePhotoUpload"
            />
          </div>

          <div>
            <label class="font-weight-medium">Add a completion note (Optional)</label>
            <v-textarea
              v-model="completeDialog.note"
              placeholder="Type here..."
              outlined
              dense
              rows="2"
            />
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="completeDialog.show = false">Cancel</v-btn>
          <v-btn
            color="green"
            variant="flat"
            :disabled="!hasCompleteDialogPhotos"
            @click="submitCompletion"
          >
            <v-icon start>mdi-check</v-icon> Mark Complete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>

  <!-- Empty state -->
  <v-card v-else class="pa-4 text-center text-grey" elevation="1">
    <v-icon size="48" class="mb-2">mdi-information-outline</v-icon>
    <div>Select a task to view details</div>
  </v-card>
</template>

<script>
import { getAllTaskComments, createTaskComment, updateTask, deleteTask as deleteTaskApi } from "@/api/tasks";
import { getPhotosByTaskId, uploadPhoto, reconcilePhotoSnapshots, revokePhotoSnapshots, snapshotsToFiles } from "@/api/photoItems";
import apiClient from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import ZoneMapSimple from "@/components/ZoneMapSimple.vue";
import CoordinateDisplay from "@/components/CoordinateDisplay.vue";
import AuditFieldTimeline from "@/components/audit/AuditFieldTimeline.vue";
import L from "leaflet";
import { markRaw } from "vue";
// See WayMap.vue: wrap every Leaflet object assigned to `this.*` in markRaw()
// so Vue's reactive Proxy doesn't break Leaflet cleanup and crash on zoom.

export default {
  name: "TaskDetailPanel",
  components: { ZoneMapSimple, CoordinateDisplay, AuditFieldTimeline },
  props: {
    task: { type: Object, default: null },
    closable: { type: Boolean, default: false },
    // If you want to turn off auto-fetching, set to false and control from parent
    autoFetch: { type: Boolean, default: true },

    assignees: {
      type: Array,
      default: () => [
        // { userId: "abc", name: "Jane Doe", iconId: 5 }
      ],
    },
    statuses: {
      type: Array,
      default: () => [],
    },
    priorities: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close", "request-edit", "completed", "comment-posted", "save", "deleted"],
  data() {
    return {
      comments: [],
      loadingComments: false,
      newComment: "",
      media: [],
      lightbox: { show: false, img: null },
      completeDialog: { show: false, snapshots: [], note: "" },
      deleteDialog: { show: false },

      // edit state
      isEditing: false,
      draft: null,

      // map handles for edit mode
      map: null,
      marker: null,
      capturingLocation: false,
    };
  },
  computed: {
    completePhotoFiles() {
      // Stable File[] derived from iOS-safe snapshots, for v-file-input chips.
      return snapshotsToFiles(this.completeDialog.snapshots);
    },
    draftNewPhotoFiles() {
      return snapshotsToFiles(this.draft?.newPhotoSnapshots || []);
    },
    hasCompleteDialogPhotos() {
      return this.completeDialog.snapshots.length > 0;
    },
    // Combined: API photos + preview of selected local files (so user sees image before Save)
    effectiveMedia() {
      const fromApi = (this.media || []).map((p) => ({
        url: p.url || p.Url,
        photoUrlId: p.photoUrlId || p.PhotoUrlId,
      }));
      const fromLocal = (this.draft?.newPhotoSnapshots || []).map((s, i) => ({
        url: s.previewUrl,
        photoUrlId: `local-preview-${i}`,
        isLocal: true,
      }));
      return [...fromApi, ...fromLocal];
    },
  },
  watch: {
    statuses: {
      immediate: true,
      handler(newVal) {
      }
    },
    priorities: {
      immediate: true,
      handler(newVal) {
      }
    },
    task: {
      immediate: true,
      async handler(t) {
        // reset state first
        this.comments = [];
        this.media = [];
        this.newComment = "";
        revokePhotoSnapshots(this.completeDialog.snapshots);
        this.completeDialog = { show: false, snapshots: [], note: "" };
        this.deleteDialog = { show: false };
        if (this.draft?.newPhotoSnapshots) {
          revokePhotoSnapshots(this.draft.newPhotoSnapshots);
        }
        if (this.autoFetch && t) {
          await Promise.allSettled([this.fetchComments(t.taskId), this.fetchMedia(t)]);
        }
        if (!this.isEditing) this.draft = null;
      },
    },
    isEditing: {
      async handler(val) {
        if (val) {
          await this.$nextTick();
          this.initEditMap();
        } else {
          this.destroyMap();
        }
      },
    },
  },
  methods: {
    async fetchComments(taskId) {
      if (!taskId) return;
      try {
        this.loadingComments = true;
        this.comments = await getAllTaskComments(taskId);
      } catch (e) {
        this.comments = [];
      } finally {
        this.loadingComments = false;
      }
    },
    async fetchMedia(task) {
      try {
        if (!task) {
          this.media = [];
          return;
        }
        const taskId = task.taskId ?? task.TaskId;
        if (taskId) {
          this.media = await getPhotosByTaskId(taskId);
        } else {
          this.media = [];
        }
      } catch (e) {
        this.media = [];
      }
    },
    openLightbox(imgUrl) {
      this.lightbox.img = imgUrl;
      this.lightbox.show = true;
    },
    // When the photo was taken — short (dd/mm/yyyy) caption, full date+time on hover.
    formatTaken(photo) {
      const raw = photo?.createdAt ?? photo?.CreatedAt;
      if (!raw) return "";
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB");
    },
    formatTakenFull(photo) {
      const raw = photo?.createdAt ?? photo?.CreatedAt;
      if (!raw) return "";
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-GB");
    },
    getUserIconUrl(id) {
      const key = String(Math.min(Math.max(parseInt(id, 10) || 1, 1), 50)).padStart(2, "0");
      return `/images/avatars/${key}.svg`;
    },
    goToFinding() {
      const id = this.task?.findingId ?? this.task?.FindingId;
      if (id) this.$router.push({ name: "FindingEdit", params: { id } });
    },
    goToAsset() {
      const id = this.task?.assetId ?? this.task?.AssetId;
      if (id) this.$router.push({ name: "AssetTasksGrid", params: { assetId: id } });
    },
    goToWorkOrder() {
      const id = this.task?.workOrderId ?? this.task?.WorkOrderId;
      if (id) this.$router.push({ name: "WorkOrder", params: { workOrderId: id } });
    },
    newId() {
      return crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(16).slice(2);
    },
    async submitComment() {
      const content = this.newComment.trim();
      if (!content || !this.task) return;
      try {
        const auth = useAuthStore();
        const newCommentDto = {
          commentId: "",
          taskId: this.task.taskId,
          comment: content,
          userId: auth.user?.profile?.sub || null,
        };
        await createTaskComment(newCommentDto);
        this.newComment = "";
        await this.fetchComments(this.task.taskId);
        this.$emit("comment-posted", { taskId: this.task.taskId });
      } catch (error) {
      }
    },
    openCompleteDialog() {
      if (!this.task) return;
      revokePhotoSnapshots(this.completeDialog.snapshots);
      this.completeDialog = { show: true, snapshots: [], note: "" };
    },
    confirmDelete() {
      if (!this.task) return;
      this.deleteDialog.show = true;
    },
    async deleteTask() {
      if (!this.task) return;
      try {
        await deleteTaskApi(this.task.taskId);
        this.deleteDialog.show = false;
        this.$emit("deleted", { taskId: this.task.taskId });
        this.$toast?.success?.("Task deleted successfully");
      } catch (error) {
        this.$toast?.error?.("Failed to delete task. Please try again.");
      }
    },
    async handlePhotoUpload(val) {
      // iOS-safe: snapshot bytes into memory immediately so iOS can't evict
      // the camera-temp file between picking and submitting.
      this.completeDialog.snapshots = await reconcilePhotoSnapshots(this.completeDialog.snapshots, val);
    },
    async onDraftNewPhotosChange(val) {
      if (!this.draft) return;
      this.draft.newPhotoSnapshots = await reconcilePhotoSnapshots(this.draft.newPhotoSnapshots, val);
    },
    async uploadToAzure(file) {
      // Route through apiClient so it hits the API host with the bearer token.
      // A relative fetch resolves against the SPA origin in prod (no API there).
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await apiClient.post("/photos/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data; // { url, ... }
    },
    async submitCompletion() {
      if (!this.task) return;
      const snaps = this.completeDialog.snapshots;
      if (!snaps.length) return;
      try {
        // 1) upload each photo and create DB row (use FindingId or TaskId)
        const payloadFor = (snap) => ({
          Blob: snap.blob,
          PhotoUrlId: crypto.randomUUID(),
          CreatedAt: new Date().toISOString(),
          ...(this.task.findingId ? { FindingId: this.task.findingId } : { TaskId: this.task.taskId }),
        });
        await Promise.all(snaps.map((snap) => uploadPhoto(payloadFor(snap))));

        // 2) update the task
        const updated = { ...this.task, comment: this.completeDialog.note, status: "3" };
        await updateTask(updated);

        // 3) refresh gallery
        await this.fetchMedia(this.task);

        revokePhotoSnapshots(this.completeDialog.snapshots);
        this.completeDialog.snapshots = [];
        this.completeDialog.show = false;
        this.$emit("completed", { taskId: this.task.taskId });
      } catch (err) {
        console.error("Task completion / photo upload failed:", err);
        this.$toast?.error?.(
          err?.response?.data?.message || err?.message ||
          "Could not complete task — some photos may not have uploaded. Please retry."
        );
      }
    },
    // ===== EDIT MODE =====
    numOrNull(v) {
      return v == null || v === "" ? null : Number(v);
    },
    asInputDate(val) {
      if (!val) return null;
      const d = new Date(val);
      if (isNaN(d)) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    beginEdit() {
      if (!this.task) return;
      this.draft = {
        taskId: this.task.taskId,
        title: this.task.title ?? "",
        description: this.task.description ?? "",
        dueDate: this.asInputDate(this.task.dueDate),
        assignedToUserId: this.task.assignedTo ?? null, // keep an id, not display name
        estimatedCost: this.task.estimatedCost ?? null,

        latitude: this.task.latitude ?? null,
        longitude: this.task.longitude ?? null,

        statusId: this.task.status != null ? Number(this.task.status) : null,
        priorityId: this.task.priority != null ? Number(this.task.priority) : null,

        newPhotoSnapshots: [], // iOS-safe snapshots of files selected during edit
      };
      this.isEditing = true;
    },

    cancelEdit() {
      if (this.draft?.newPhotoSnapshots) {
        revokePhotoSnapshots(this.draft.newPhotoSnapshots);
      }
      this.isEditing = false;
      this.draft = null;
    },
    goToFinding() {
      const id = this.task?.findingId ?? this.task?.FindingId;
      if (id) this.$router.push({ name: "FindingEdit", params: { id } });
    },
    goToAsset() {
      const id = this.task?.assetId ?? this.task?.AssetId;
      if (id) this.$router.push({ path: "/assets", query: { assetId: id } });
    },
    goToWorkOrder() {
      const id = this.task?.workOrderId ?? this.task?.WorkOrderId;
      if (id) this.$router.push({ name: "WorkOrder", params: { workOrderId: id } });
    },

    async saveEdit() {
      if (!this.task || !this.draft) return;

      // Keep selected files ready; uploads run only after updateTask(payload) succeeds.
      const newSnaps = this.draft.newPhotoSnapshots || [];

      // detect if due date actually changed
      const originalDue = this.asInputDate(this.task.dueDate);
      const nextDueDate = this.draft.dueDate || null;
      const changedDue = nextDueDate !== originalDue;

      const payload = {
        taskId: this.task.taskId,
        title: (this.draft.title ?? "").trim(),
        description: (this.draft.description ?? "").trim(),

        assignedTo: this.draft.assignedToUserId ?? null,
        estimatedCost: this.draft.estimatedCost != null ? Number(this.draft.estimatedCost) : null,
        latitude: this.draft.latitude != null ? Number(this.draft.latitude) : null,
        longitude: this.draft.longitude != null ? Number(this.draft.longitude) : null,
        // optionally: photos: uploads
        status: this.draft.statusId != null ? Number(this.draft.statusId) : null,
        priority: this.draft.priorityId != null ? Number(this.draft.priorityId) : null,
        // send date-only string if it changed; otherwise omit so server won’t update it
        ...(changedDue ? { dueDate: nextDueDate } : {}),
      };

      await updateTask(payload);

      let uploadHadError = false;
      if (newSnaps.length) {
        try {
          await Promise.all(
            newSnaps.map((snap) =>
              uploadPhoto({
                Blob: snap.blob,
                PhotoUrlId: this.newId(),
                ...(this.task.findingId ? { FindingId: this.task.findingId } : { TaskId: this.task.taskId }),
                CreatedAt: new Date().toISOString(),
              })
            )
          );
        } catch (error) {
          uploadHadError = true;
        }
      }

      revokePhotoSnapshots(newSnaps);
      await this.fetchMedia(this.task);

      // emit for parent to call updateTask(...) and refresh (keeps API in one place)
      this.$emit("save", payload);
      if (uploadHadError) {
        this.$toast?.error?.("Task saved, but some images could not be uploaded.");
      }

      this.isEditing = false;
      this.draft = null;
    },
    statusById(id) {
      return this.statuses.find((s) => String(s.statusId || s.StatusId) === String(id));
    },
    priorityById(id) {
      return this.priorities.find((p) => String(p.priorityId || p.PriorityId) === String(id));
    },

    statusLabel(row) {
      // prefer FK label if present, else lookup by id
      const status = this.statusById(row?.status);
      return row?.statusFK || status?.status || status?.Status || "—";
    },
    priorityLabel(row) {
      const priority = this.priorityById(row?.priority);
      return row?.priorityFK || priority?.priority || priority?.Priority || "—";
    },

    statusColorChip(row) {
      const byId = this.statusById(row?.status);
      if (byId?.colour || byId?.Colour) return String(byId.colour || byId.Colour);
      if (row?.statusColour) return String(row.statusColour);
      return "grey";
    },
    priorityColorChip(row) {
      const byId = this.priorityById(row?.priority);
      if (byId?.colour || byId?.Colour) return String(byId.colour || byId.Colour);
      if (row?.priorityColour) return String(row.priorityColour);
      return "grey";
    },
    formatDateTime(val, opts) {
      if (!val) return "-";
      let d = val;
      if (typeof val === "string" || typeof val === "number") {
        d = new Date(val);
      }
      if (!(d instanceof Date) || isNaN(d.getTime())) return "-";
      try {
        return new Intl.DateTimeFormat(undefined, {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          ...(opts || {}),
        }).format(d);
      } catch {
        return d.toLocaleString?.() || "-";
      }
    },
    formatDate(val) {
      if (!val) return "-";
      let d = val;
      if (typeof val === "string" || typeof val === "number") {
        d = new Date(val);
      }
      if (!(d instanceof Date) || isNaN(d.getTime())) return "-";
      try {
        return new Intl.DateTimeFormat(undefined, {
          year: "numeric",
          month: "short",
          day: "2-digit",
        }).format(d);
      } catch {
        return d.toLocaleDateString?.() || "-";
      }
    },
    // ===== Editable Map (Leaflet) =====
    initEditMap() {
      const el = this.$refs.editMap;
      if (!el) return;

      // destroy old just in case
      this.destroyMap();

      const lat = Number(this.draft?.latitude ?? this.task?.latitude ?? 53.5);
      const lon = Number(this.draft?.longitude ?? this.task?.longitude ?? -8.0);

      this.map = markRaw(L.map(el).setView([lat, lon], 12));
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(this.map);

      this.marker = markRaw(L.marker([lat, lon], { draggable: true }).addTo(this.map));

      this.map.on("click", (e) => {
        this.marker.setLatLng(e.latlng);
        this.applyMarker(e.latlng);
      });

      this.marker.on("dragend", () => {
        const ll = this.marker.getLatLng();
        this.applyMarker(ll);
      });

      setTimeout(() => this.map?.invalidateSize(), 0);
    },

    applyMarker(ll) {
      if (!this.draft) return;
      this.draft.latitude = Number(ll.lat.toFixed(6));
      this.draft.longitude = Number(ll.lng.toFixed(6));
    },

    async captureLocation() {
      if (!navigator.geolocation) {
        this.$toast?.error?.("Geolocation is not supported");
        return;
      }
      this.capturingLocation = true;
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0,
          });
        });
        if (this.draft) {
          this.draft.latitude = pos.coords.latitude;
          this.draft.longitude = pos.coords.longitude;
          if (this.marker) this.marker.setLatLng([pos.coords.latitude, pos.coords.longitude]);
          if (this.map) this.map.setView([pos.coords.latitude, pos.coords.longitude]);
        }
        this.$toast?.success?.("Location captured");
      } catch (err) {
        this.$toast?.error?.(err.message || "Could not get location");
      } finally {
        this.capturingLocation = false;
      }
    },

    destroyMap() {
      if (this.map) {
        this.map.off();
        this.map.remove();
        this.map = null;
        this.marker = null;
      }
    },
  },
  beforeUnmount() {
    revokePhotoSnapshots(this.completeDialog?.snapshots);
    revokePhotoSnapshots(this.draft?.newPhotoSnapshots);
  },
};
</script>

<style scoped>
.task-detail-shell {
  border-radius: 24px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.96), rgba(255, 255, 255, 0.99));
  box-shadow: 0 24px 44px rgba(15, 23, 42, 0.07);
}

.task-detail-header {
  padding-bottom: 18px !important;
}

.task-detail__eyebrow {
  font-size: 0.84rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 700;
  color: #64748b;
}

.task-detail__title {
  color: #1f2937;
}

.task-detail__header-actions :deep(.v-btn) {
  border-radius: 12px;
}

.task-detail__action-btn {
  min-height: 36px;
  padding-inline: 14px;
  border-radius: 12px;
}

.task-detail__action-btn--complete {
  box-shadow: 0 10px 22px rgba(34, 197, 94, 0.18);
}

.task-detail__link-row {
  padding: 0 4px;
}

.task-detail__breadcrumbs {
  padding: 0 4px;
}

.task-detail__meta-row {
  padding: 0 4px;
}

.detail-section-card {
  border-radius: 18px;
  border-color: rgba(148, 163, 184, 0.18) !important;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.05);
}

.fs-overline {
  font-size: 1.2rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  position: relative;
  left: -2px;
  font-weight: 100;
  color: rgba(0, 0, 0, 0.54);
}

.text-subtitle-2 {
  color: grey;
}

.caption {
  font-size: 0.9em !important;
}
.text-caption {
  font-size: 0.7em !important;
}
.text-body-1 {
  font-size: 1.2em !important;
}
::v-deep(.map-container.leaflet-container.leaflet-touch) {
  height: 250px;
  margin-bottom: 45px;
}

.header-line {
  gap: 12px;
}
.title {
  flex: 1 1 auto;
  min-width: 0;
}
.v-breadcrumbs--density-default {
  padding-top: 0px;
  padding-bottom: 0px;
}

.header-accent {
  height: 3px;
  border-radius: 2px;
  margin: 0 -16px;
}

.header-accent--strong {
  height: 4px;
  border-radius: 999px;
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.18);
}

.comments-pane {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.comments-pane::-webkit-scrollbar {
  width: 6px;
}

.comments-pane::-webkit-scrollbar-track {
  background: transparent;
}

.comments-pane::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.comments-pane::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

/* Long comments (up to 1000 chars) need to wrap and not be truncated by
   v-list-item-title's default single-line ellipsis. */
.comment-body {
  flex: 1 1 auto;
  min-width: 0; /* allow flex item to shrink so word-break can act */
}
.comment-text {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}

@media (max-width: 960px) {
  .task-detail-shell {
    border-radius: 18px;
  }
}
</style>
